import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiPlay,
  FiBookOpen,
  FiCalendar,
  FiTarget,
  FiLoader,
  FiFilter,
  FiUsers,
  FiLogIn,
  FiSearch,
} from "react-icons/fi";
import { getExaminationList } from "../../../../services/student-Services";
import { getLocalStorageData } from "../../../../services/axiosComponent";
import RegexPatterns from "../../../../services/regex.js";

// Utility function to parse API date format (DD/MM/YYYY HH:MM) and convert from UTC to IST
const parseApiDate = (dateString) => {
  if (!dateString) return null;

  try {
    const [datePart, timePart] = dateString.split(" ");
    const [day, month, year] = datePart.split("/");
    const [hours, minutes] = timePart.split(":");

    // Create UTC date object - treat the input as UTC time
    const utcDate = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00.000Z`);

    console.log(`Original UTC from backend: ${dateString} -> Parsed UTC: ${utcDate.toISOString()}`);
    
    // The date object will automatically be in local browser timezone
    // But we want to display it as IST, so we'll use toLocaleString with IST timezone
    return utcDate;
  } catch (error) {
    console.error("Error parsing date:", dateString, error);
    return new Date(); // fallback to current date
  }
};

// Utility function to convert any date to IST for display
const toISTString = (date) => {
  if (!date) return '';
  return date.toLocaleString('en-IN', { 
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

// Utility function to get current time for comparisons (in local timezone)
const getCurrentTime = () => {
  return new Date();
};

// Transform API data to component format
const transformApiDataToExamFormat = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) return [];

  return apiData.map((item) => {
    const startTime = parseApiDate(item["START TIME"]);
    const endTime = parseApiDate(item["END TIME"]);

    // Calculate duration in minutes from the difference between start and end time
    let durationInMinutes = 60; // default duration in minutes
    let durationDisplay = "1h 0m"; // default display format
    
    if (startTime && endTime) {
      // For same day exams where end time appears before start time (like 11:51 to 03:51)
      // This means the exam runs within the same day but crosses midnight conceptually
      // But actually it's same day from 11:51 AM to 3:51 AM - so it's about 4 hours
      
      let calculatedEndTime = endTime;
      
      // If end time is before start time on the same day, add 24 hours to end time
      if (endTime.getTime() <= startTime.getTime()) {
        calculatedEndTime = new Date(endTime.getTime() + 24 * 60 * 60 * 1000);
      }

      const durationMs = calculatedEndTime.getTime() - startTime.getTime();
      durationInMinutes = Math.round(durationMs / (1000 * 60)); // convert milliseconds to minutes

      // Ensure minimum duration of 1 minute
      durationInMinutes = Math.max(durationInMinutes, 1);
      
      // Convert minutes to hours and minutes for display
      const hours = Math.floor(durationInMinutes / 60);
      const minutes = durationInMinutes % 60;
      
      if (hours > 0 && minutes > 0) {
        durationDisplay = `${hours}h ${minutes}m`;
      } else if (hours > 0) {
        durationDisplay = `${hours}h`;
      } else {
        durationDisplay = `${minutes}m`;
      }
      
      console.log(`Exam "${item["NAME"]}" - UTC Start: ${item["START TIME"]}, UTC End: ${item["END TIME"]}`);

    }

    return {
      id: item["_ID"] || `exam_${Date.now()}_${Math.random()}`,
      seriesName: item["NAME"] || "Unnamed Exam",
      subject: item["SUBJECT"] || "General",
      totalMarks: 100, // Mock data - will be added from backend later
      examType: item["EXAM TYPE"] || "Objective",
      startTime: startTime || new Date(),
      duration: durationInMinutes, // Keep in minutes for calculations
      durationDisplay: durationDisplay, // Add formatted display string
      status: item["STATUS"] || "Pending", // Use actual API status
      description: item["DESCRIPTION"] || "",
      examMode: item["EXAM MODE"] || "Online",
      series: item["SERIES"] || null,
      academicYear: item["ACADEMIC YEAR"] || "",
      endTime: endTime,
      calculatedEndTime: endTime && startTime && endTime.getTime() <= startTime.getTime() 
        ? new Date(endTime.getTime() + 24 * 60 * 60 * 1000) 
        : endTime, // Store the calculated end time for time comparisons
      // Keep original API data for reference
      _originalData: item,
      ...item, // Spread to include all other fields
    };
  });
};

// Mock stats data (as requested to keep mock until new API is provided)
const mockStatsData = {
  total: 0,
  pending: 0,
  submitted: 0,
  ongoing: 0,
};

const CountdownTimer = ({ targetTime, onTimeUp }) => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const updateTimer = () => {
      const now = getCurrentTime(); // Use current time for countdown
      const target = new Date(targetTime).getTime();
      const difference = target - now.getTime();

      if (difference > 0) {
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor(
          (difference % (1000 * 60 * 60)) / (1000 * 60)
        );
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ hours, minutes, seconds });
        setIsExpired(false);
      } else {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        setIsExpired(true);
        onTimeUp && onTimeUp();
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [targetTime, onTimeUp]);

  if (isExpired) {
    return <span className="countdown-expired">Time's Up!</span>;
  }

  return (
    <span className="countdown-timer">
      {String(timeLeft.hours).padStart(2, "0")}:
      {String(timeLeft.minutes).padStart(2, "0")}:
      {String(timeLeft.seconds).padStart(2, "0")}
    </span>
  );
};

const ExamCard = ({ exam }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleJoinExam = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      console.log(`Joining exam: ${exam.seriesName}`);
    }, 1500);
  };

  // Determine if exam can be joined based on time
  const getExamTimeStatus = () => {
    const now = getCurrentTime(); // Current time for comparison
    const startTime = new Date(exam.startTime);
    const endTime = exam.calculatedEndTime ? new Date(exam.calculatedEndTime) : new Date(exam.endTime);

    console.log('=== EXAM TIME STATUS CHECK ===');
    console.log('Current time (local):', now.toLocaleString());
    console.log('Current time (IST):', toISTString(now));
    console.log('Start time (UTC):', startTime.toISOString());
    console.log('Start time (IST):', toISTString(startTime));
    console.log('End time (UTC):', endTime.toISOString());
    console.log('End time (IST):', toISTString(endTime));

    // Compare UTC times directly for accuracy
    if (now >= startTime && now <= endTime) {
      console.log('✅ CAN JOIN - Current time is between start and end time');
      return "canJoin";
    } else if (now < startTime) {
      console.log('⏳ NOT STARTED - Current time is before start time');
      return "notStarted";
    } else if (now > endTime) {
      console.log('❌ ENDED - Current time is after end time');
      return "ended";
    }

    return "notStarted";
  };

  // Get display status for the badge (use actual API status)
  const getDisplayStatus = () => {
    const apiStatus = exam.status?.toLowerCase() || "pending";

    // Map API status to display format
    switch (apiStatus) {
      case "pending":
        return "pending";
      case "ongoing":
      case "active":
        return "ongoing";
      case "completed":
      case "submitted":
        return "submitted";
      case "expired":
        return "expired";
      default:
        return apiStatus;
    }
  };

  const timeStatus = getExamTimeStatus();
  const displayStatus = getDisplayStatus();
  const canJoin =
    timeStatus === "canJoin" &&
    (displayStatus === "pending" || displayStatus === "ongoing");

  return (
    <div className={`exam-card ${displayStatus}`}
    
    onClick={()=>{
      const encodedData = encodeURIComponent(JSON.stringify(exam));
  const url = `/student/dashboard/exam?data=${encodedData}`;
   window.open(url, "_blank", "width=1200,height=800");
    }}
    >
      <div className="exam-card-header">
        <div className="exam-title-section">
          <h3 className="exam-title">{exam.seriesName}</h3>
          {exam.description && (
            <p className="exam-description-text">{exam.description}</p>
          )}
          <p className="exam-subject">{exam.subject}</p>
          {exam.series && <p className="exam-series">{exam.series}</p>}
        </div>
        <span className={`status-badge status-${displayStatus}`}>
          {displayStatus === "pending" && <FiClock size={10} />}
          {displayStatus === "ongoing" && <FiLoader size={10} />}
          {displayStatus === "submitted" && <FiCheckCircle size={10} />}
          {displayStatus === "expired" && <FiAlertCircle size={10} />}
          {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
        </span>
      </div>

      <div className="exam-meta-row">
        <div className="meta-item">
          <FiTarget size={12} />
          <span>{exam.totalMarks}pts</span>
        </div>
        <div className="meta-item">
          <FiClock size={12} />
          <span>{exam.durationDisplay}</span>
        </div>
        <div className="meta-item">
          <span className="exam-type">{exam.examType}</span>
        </div>
        <div className="meta-item">
          <span className="exam-mode">{exam.examMode}</span>
        </div>
      </div>

      <div className="exam-schedule">
        <div className="schedule-info">
          <FiCalendar size={12} />
          <span>
            {new Date(exam.startTime).toLocaleDateString("en-IN", {
              month: "short",
              day: "numeric",
              year: "numeric",
              timeZone: "Asia/Kolkata"
            })}{" "}
            at{" "}
            {new Date(exam.startTime).toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
              timeZone: "Asia/Kolkata"
            })} IST
          </span>
        </div>
      </div>

      {(timeStatus === "notStarted" || timeStatus === "canJoin") && (
        <div
          className={`countdown-section ${
            timeStatus === "canJoin" ? "ongoing" : ""
          }`}
        >
          <span className="countdown-label">
            {timeStatus === "notStarted" ? "Starts in" : "Time left"}
          </span>
          <CountdownTimer
            targetTime={
              timeStatus === "notStarted" 
                ? exam.startTime 
                : (exam.calculatedEndTime || exam.endTime)
            }
            onTimeUp={() => {
              // Force re-render by updating a state or triggering parent re-render
              // This will help update the button states when time changes
              console.log(`Timer expired for exam: ${exam.seriesName}`);
            }}
          />
        </div>
      )}

      <div className="exam-card-footer">
        {displayStatus === "submitted" || displayStatus === "completed" ? (
          <button className="exam-btn completed" disabled>
            <FiCheckCircle size={14} />
            Completed
          </button>
        ) : timeStatus === "ended" || displayStatus === "expired" ? (
          <button className="exam-btn expired" disabled>
            <FiAlertCircle size={14} />
            {timeStatus === "ended" ? "Exam Ended" : "Expired"}
          </button>
        ) : timeStatus === "canJoin" ? (
          <button
            className="exam-btn join"
            onClick={handleJoinExam}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FiLoader className="spinning" size={14} />
                Joining...
              </>
            ) : (
              <>
                <FiLogIn size={14} />
                Join Exam
              </>
            )}
          </button>
        ) : timeStatus === "notStarted" ? (
          <button className="exam-btn disabled" disabled>
            <FiClock size={14} />
            Not Started
          </button>
        ) : (
          <button className="exam-btn disabled" disabled>
            <FiClock size={14} />
            Not Available
          </button>
        )}
      </div>
    </div>
  );
};

const StatsCard = ({ icon: Icon, title, value, color }) => (
  <div className={`stats-card ${color}`}>
    <div className="stats-icon">
      <Icon size={18} />
    </div>
    <div className="stats-content">
      <div className="stats-value">{value}</div>
      <div className="stats-title">{title}</div>
    </div>
  </div>
);

const LoadingSkeleton = () => (
  <div className="loading-skeleton">
    {[...Array(6)].map((_, index) => (
      <div key={index} className="skeleton-card">
        <div className="skeleton-header">
          <div className="skeleton-title"></div>
          <div className="skeleton-status"></div>
        </div>
        <div className="skeleton-meta"></div>
        <div className="skeleton-footer"></div>
      </div>
    ))}
  </div>
);

const DashBoardExamListTable = ({ userId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [examData, setExamData] = useState([]);
  const [filteredExamData, setFilteredExamData] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Search timeout ref (same logic as MasterTable)
  const searchTimeoutRef = useRef(null);

  const fetchExams = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await getExaminationList({
        userId,
        organizationId:
          getLocalStorageData("profileDetails")["_ORGANIZATION_"]["_ID"],
      });

      if (response.status === "success" || response.status === "200") {
        // Handle both possible response formats
        const entityList =
          response.data?.entityList || response.response?.entityList || [];
        const transformedData = transformApiDataToExamFormat(entityList);
        setExamData(transformedData);
        setFilteredExamData(transformedData); // Initialize filtered data
      } else {
        setError(response.message || "Failed to fetch exams");
        setExamData([]); // Set empty array on error
        setFilteredExamData([]);
      }
    } catch (error) {
      console.error("Error fetching exams:", error);
      setError("Failed to fetch exams. Please try again.");
      setExamData([]); // Set empty array on error
      setFilteredExamData([]);
    }
    setIsLoading(false);
  };

  // Search functionality with proper space handling
  const handleSearch = useCallback(
    (e) => {
      let value = e.target.value;
      
      // Prevent spaces at the beginning
      if (value.startsWith(' ')) {
        value = value.trimStart();
      }
      
      // Replace multiple consecutive spaces with single space
      value = value.replace(/\s+/g, ' ');
      
      // Update the input value to reflect the cleaned value
      e.target.value = value;
      setSearchQuery(value);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Trim for actual search but keep the display value with single spaces
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        setFilteredExamData(examData);
        setSearchLoading(false);
        return;
      }

      if (RegexPatterns.SEARCH_NAME_REGEXP.test(trimmedValue)) {
        setSearchLoading(true);

        searchTimeoutRef.current = setTimeout(() => {
          const filtered = examData.filter((exam) =>
            exam.seriesName.toLowerCase().includes(trimmedValue.toLowerCase()) ||
            exam.subject.toLowerCase().includes(trimmedValue.toLowerCase()) ||
            exam.description.toLowerCase().includes(trimmedValue.toLowerCase()) ||
            exam.examType.toLowerCase().includes(trimmedValue.toLowerCase()) ||
            exam.examMode.toLowerCase().includes(trimmedValue.toLowerCase())
          );
          
          setFilteredExamData(filtered);
          setSearchLoading(false);
        }, 500);
      }
    },
    [examData]
  );

  // Update filtered data when examData changes
  useEffect(() => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      setFilteredExamData(examData);
    } else {
      // Re-apply search filter when data changes
      const filtered = examData.filter((exam) =>
        exam.seriesName.toLowerCase().includes(trimmedQuery.toLowerCase()) ||
        exam.subject.toLowerCase().includes(trimmedQuery.toLowerCase()) ||
        exam.description.toLowerCase().includes(trimmedQuery.toLowerCase()) ||
        exam.examType.toLowerCase().includes(trimmedQuery.toLowerCase()) ||
        exam.examMode.toLowerCase().includes(trimmedQuery.toLowerCase())
      );
      setFilteredExamData(filtered);
    }
  }, [examData, searchQuery]);

  useEffect(() => {
    fetchExams();
    
    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [userId]);

  // Calculate stats from actual filtered data
  const calculateStats = (exams) => {
    if (!exams || exams.length === 0) {
      return { total: 0, pending: 0, submitted: 0, ongoing: 0 };
    }

    const stats = exams.reduce(
      (acc, exam) => {
        acc.total++;
        const status = exam.status?.toLowerCase() || "pending";

        if (status === "pending") {
          acc.pending++;
        } else if (status === "submitted" || status === "completed") {
          acc.submitted++;
        } else if (status === "ongoing") {
          acc.ongoing++;
        }

        return acc;
      },
      { total: 0, pending: 0, submitted: 0, ongoing: 0 }
    );

    return stats;
  };

  const stats = calculateStats(filteredExamData);

  if (isLoading) {
    return (
      <div className="exam-dashboard">
        <div className="section-header">
          <h2 className="section-title">Your Exams</h2>
        </div>
        <div className="stats-section">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="skeleton-stats-card">
              <div className="skeleton-icon"></div>
              <div className="skeleton-stats-content">
                <div className="skeleton-number"></div>
                <div className="skeleton-label"></div>
              </div>
            </div>
          ))}
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="exam-dashboard">
        <div className="section-header">
          <h2 className="section-title">Your Exams</h2>
        </div>
        <div className="error-state">
          <FiAlertCircle size={32} className="error-icon" />
          <h3>Error Loading Exams</h3>
          <p>{error}</p>
          <button onClick={fetchExams} className="retry-btn">
            <FiLoader size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="exam-dashboard">
      {/* Header with Title, Search, and Filter */}
      <div className="section-header">
        <div className="header-content">
          <h2 className="section-title">Your Exams</h2>
          {stats.pending > 0 && (
            <div className="pending-badge">
              <span className="pending-count">{stats.pending}</span>
              <span className="pending-text">pending</span>
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <div className="search-icon">
              {searchLoading ? (
                <FiLoader className="search-spinner" size={16} />
              ) : (
                <FiSearch size={16} />
              )}
            </div>
            <input
              type="text"
              placeholder="Search exams..."
              value={searchQuery}
              onChange={handleSearch}
              className="search-input"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setFilteredExamData(examData);
                }}
                className="clear-search-btn"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <button className="filter-btn">
          <FiFilter size={16} />
        </button>
      </div>

      {/* Stats Section */}
      <div className="stats-section">
        <StatsCard
          icon={FiBookOpen}
          title="Total"
          value={stats.total}
          color="blue"
        />
        <StatsCard
          icon={FiClock}
          title="Pending"
          value={stats.pending}
          color="orange"
        />
        <StatsCard
          icon={FiCheckCircle}
          title="Completed"
          value={stats.submitted}
          color="green"
        />
      </div>

      {/* Search Results Info */}
      {searchQuery.trim() && (
        <div className="search-results-info">
          <span>
            {searchLoading 
              ? "Searching..." 
              : `${filteredExamData.length} exam${filteredExamData.length !== 1 ? 's' : ''} found for "${searchQuery.trim()}"`
            }
          </span>
        </div>
      )}

      {/* Exam Cards */}
      <div className="exam-cards-grid">
        {filteredExamData.map((exam) => (
          <ExamCard key={exam.id} exam={exam} />
        ))}
      </div>

      {/* Empty States */}
      {filteredExamData.length === 0 && !isLoading && !error && (
        <div className="empty-state">
          {searchQuery.trim() ? (
            <>
              <FiSearch size={32} className="empty-icon" />
              <h3>No exams found</h3>
              <p>Try adjusting your search terms or clear the search to see all exams.</p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setFilteredExamData(examData);
                }}
                className="clear-search-empty-btn"
              >
                Clear Search
              </button>
            </>
          ) : (
            <>
              <FiBookOpen size={32} className="empty-icon" />
              <h3>No exams available</h3>
              <p>Check back later for upcoming exams.</p>
            </>
          )}
        </div>
      )}

      <style jsx>{`
        .exam-dashboard {
          display: flex;
          flex-direction: column;
          background: #fafbfc;
          border-radius: 16px;
          overflow: hidden;
          height: 100%;
          border: 1px solid #e2e8f0;
        }

        /* Header Section */
        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.25rem 1.5rem 1rem;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-bottom: 1px solid #f1f5f9;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .section-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .pending-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
          padding: 0 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          color: white;
          background: #f59e0b;
        }

        .pending-count {
          display: contents;
        }

        .pending-text {
          display: none;
        }

        /* Search Container - Enhanced Responsiveness */
        .search-container {
          flex: 1;
          max-width: 320px;
          min-width: 220px;
        }

        .search-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          height: 42px;
        }

        .search-input-wrapper:focus-within {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .search-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 0.75rem;
          color: #64748b;
          flex-shrink: 0;
        }

        .search-spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .search-input {
          flex: 1;
          padding: 0.75rem 0.5rem 0.75rem 0;
          border: none;
          background: transparent;
          font-size: 0.875rem;
          color: #1e293b;
          outline: none;
          min-width: 0;
        }

        .search-input::placeholder {
          color: #94a3b8;
        }

        .clear-search-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          margin-right: 0.5rem;
          background: #f1f5f9;
          border: none;
          border-radius: 50%;
          color: #64748b;
          cursor: pointer;
          font-size: 16px;
          line-height: 1;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .clear-search-btn:hover {
          background: #e2e8f0;
          color: #475569;
        }

        .filter-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .filter-btn:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
          color: #475569;
          transform: translateY(-1px);
        }

        /* Search Results Info */
        .search-results-info {
          padding: 0.75rem 1.5rem;
          background: #f8fafc;
          border-bottom: 1px solid #f1f5f9;
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 500;
        }

        /* Statistics Section */
        .stats-section {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          padding: 0 1rem 0.75rem;
        }

        /* Stats Card */
        .stats-card {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0.75rem;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .stats-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .stats-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .stats-card.blue .stats-icon {
          background: #3b82f6;
        }

        .stats-card.orange .stats-icon {
          background: #f59e0b;
        }

        .stats-card.green .stats-icon {
          background: #10b981;
        }

        .stats-content {
          flex: 1;
          min-width: 0;
        }

        .stats-value {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          line-height: 1;
          margin-bottom: 0.125rem;
        }

        .stats-title {
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 400;
          line-height: 1.2;
        }

        /* Exam Cards Grid */
        .exam-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
          padding: 20px;
          overflow-y: auto;
          flex: 1;
          background: #fafbfc;
        }

        .exam-cards-grid::-webkit-scrollbar {
          width: 6px;
        }

        .exam-cards-grid::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }

        .exam-cards-grid::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        /* Exam Card */
        .exam-card {
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          transition: all 0.2s ease;
          overflow: hidden;
          height: fit-content;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }

        .exam-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
          border-color: #cbd5e1;
        }

        .exam-card-header {
          padding: 16px 16px 12px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }

        .exam-title-section {
          flex: 1;
          min-width: 0;
        }

        .exam-title {
          font-size: 15px;
          font-weight: 600;
          color: #0f172a;
          margin: 0 0 6px 0;
          line-height: 1.3;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .exam-description-text {
          font-size: 12px;
          color: #6b7280;
          margin: 0 0 6px 0;
          line-height: 1.4;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .exam-subject {
          font-size: 12px;
          color: #64748b;
          margin: 0 0 2px 0;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .exam-series {
          font-size: 11px;
          color: #94a3b8;
          margin: 0;
          font-weight: 400;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .status-badge {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 0.2rem 0.4rem;
          border-radius: 6px;
          font-size: 0.65rem;
          font-weight: 500;
          border: 1px solid;
          white-space: nowrap;
        }

        .status-pending {
          background-color: #fff7ed;
          color: #ea580c;
          border-color: #fed7aa;
        }

        .status-ongoing {
          background-color: #dbeafe;
          color: #1e40af;
          border-color: #bfdbfe;
        }

        .status-submitted {
          background-color: #f0fdf4;
          color: #16a34a;
          border-color: #bbf7d0;
        }

        .status-expired {
          background-color: #fef2f2;
          color: #dc2626;
          border-color: #fecaca;
        }

        .exam-meta-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 16px 12px;
          gap: 8px;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #64748b;
          font-weight: 500;
        }

        .meta-item svg {
          color: #94a3b8;
        }

        .exam-type,
        .exam-mode {
          background: #f1f5f9;
          color: #475569;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .exam-mode {
          background: #eef2ff;
          color: #3730a3;
        }

        .exam-schedule {
          padding: 0 16px 12px;
        }

        .schedule-info {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #475569;
          background: #f8fafc;
          padding: 8px 10px;
          border-radius: 6px;
          font-weight: 500;
          margin-bottom: 8px;
        }

        .schedule-info svg {
          color: #64748b;
        }

        .countdown-section {
          margin: 0 16px 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 10px 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .countdown-section.ongoing {
          background: #fff7ed;
          border-color: #fed7aa;
        }

        .countdown-label {
          font-size: 11px;
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .countdown-timer {
          font-family: "Monaco", "Menlo", monospace;
          font-size: 13px;
          font-weight: 700;
          color: #0f172a;
          background: white;
          padding: 4px 8px;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .countdown-expired {
          color: #ef4444;
          font-weight: 700;
          font-size: 11px;
          text-transform: uppercase;
        }

        .exam-card-footer {
          padding: 16px;
          background: #fafbfc;
          border-top: 1px solid #f1f5f9;
        }

        .exam-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 0.75rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
        }

        .exam-btn.join {
          background: #059669;
          color: white;
        }

        .exam-btn.join:hover:not(:disabled) {
          background: #047857;
          transform: translateY(-1px);
        }

        .exam-btn.completed {
          background: #10b981;
          color: white;
          cursor: default;
        }

        .exam-btn.expired {
          background: #ef4444;
          color: white;
          cursor: default;
        }

        .exam-btn.disabled {
          background: #f1f5f9;
          color: #94a3b8;
          cursor: not-allowed;
          border: 1px solid #e2e8f0;
        }

        .exam-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        /* Empty State */
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 48px 24px;
          color: #64748b;
          background: white;
          margin: 20px;
          border-radius: 12px;
          border: 2px dashed #e2e8f0;
        }

        .empty-icon {
          margin-bottom: 16px;
          opacity: 0.5;
        }

        .empty-state h3 {
          margin: 0 0 8px 0;
          color: #374151;
          font-size: 16px;
          font-weight: 600;
        }

        .empty-state p {
          margin: 0 0 16px 0;
          font-size: 14px;
        }

        .clear-search-empty-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 0.75rem 1.5rem;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .clear-search-empty-btn:hover {
          background: #2563eb;
        }

        /* Error State */
        .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 48px 24px;
          color: #64748b;
          background: white;
          margin: 20px;
          border-radius: 12px;
          border: 2px dashed #fecaca;
        }

        .error-icon {
          margin-bottom: 16px;
          color: #ef4444;
        }

        .error-state h3 {
          margin: 0 0 8px 0;
          color: #374151;
          font-size: 16px;
          font-weight: 600;
        }

        .error-state p {
          margin: 0 0 16px 0;
          font-size: 14px;
        }

        .retry-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 0.75rem 1.5rem;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .retry-btn:hover {
          background: #2563eb;
        }

        /* Loading Skeletons */
        .loading-skeleton {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
          padding: 20px;
        }

        .skeleton-card {
          background: white;
          border-radius: 12px;
          padding: 16px;
          border: 1px solid #e2e8f0;
        }

        .skeleton-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }

        .skeleton-title {
          height: 16px;
          width: 60%;
          background: #e2e8f0;
          border-radius: 4px;
          animation: pulse 1.5s ease-in-out infinite;
        }

        .skeleton-status {
          height: 20px;
          width: 60px;
          background: #e2e8f0;
          border-radius: 10px;
          animation: pulse 1.5s ease-in-out infinite;
        }

        .skeleton-meta {
          height: 32px;
          background: #e2e8f0;
          border-radius: 6px;
          margin-bottom: 12px;
          animation: pulse 1.5s ease-in-out infinite;
        }

        .skeleton-footer {
          height: 40px;
          background: #e2e8f0;
          border-radius: 8px;
          animation: pulse 1.5s ease-in-out infinite;
        }

        .skeleton-stats-card {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px;
        }

        .skeleton-icon {
          width: 36px;
          height: 36px;
          background: #e2e8f0;
          border-radius: 10px;
          animation: pulse 1.5s ease-in-out infinite;
        }

        .skeleton-stats-content {
          flex: 1;
        }

        .skeleton-number {
          height: 20px;
          width: 40px;
          background: #e2e8f0;
          border-radius: 4px;
          margin-bottom: 4px;
          animation: pulse 1.5s ease-in-out infinite;
        }

        .skeleton-label {
          height: 12px;
          width: 60px;
          background: #e2e8f0;
          border-radius: 4px;
          animation: pulse 1.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .exam-cards-grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          }
          
          .search-container {
            max-width: 280px;
            min-width: 200px;
          }
        }

        @media (max-width: 768px) {
          .section-header {
            padding: 1rem;
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .header-content {
            width: 100%;
            justify-content: space-between;
          }

          .search-container {
            order: 3;
            width: 100%;
            max-width: 100%;
            min-width: auto;
          }

          .filter-btn {
            order: 2;
          }

          .stats-section {
            grid-template-columns: 1fr;
            gap: 10px;
            padding: 0 1rem 1rem;
          }

          .exam-cards-grid {
            grid-template-columns: 1fr;
            gap: 12px;
            padding: 16px;
          }

          .exam-meta-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
          }

          .meta-item {
            justify-content: space-between;
            width: 100%;
          }

          .search-results-info {
            padding: 0.75rem 1rem;
          }

          .search-input-wrapper {
            height: 40px;
          }

          .search-input {
            font-size: 0.85rem;
          }
        }

        @media (max-width: 640px) {
          .section-header {
            flex-direction: column;
            align-items: stretch;
          }

          .header-content {
            justify-content: space-between;
            margin-bottom: 0.5rem;
          }

          .search-container {
            order: 2;
            margin-bottom: 0.5rem;
          }

          .filter-btn {
            order: 3;
            align-self: flex-end;
            width: 40px;
            height: 40px;
          }

          .search-input-wrapper {
            height: 38px;
          }

          .search-input {
            font-size: 0.8rem;
            padding: 0.65rem 0.4rem 0.65rem 0;
          }

          .search-icon {
            padding: 0 0.65rem;
          }

          .clear-search-btn {
            width: 22px;
            height: 22px;
            margin-right: 0.4rem;
          }
        }

        @media (max-width: 480px) {
          .section-title {
            font-size: 18px;
          }

          .exam-card-header,
          .exam-card-footer {
            padding: 12px;
          }

          .exam-meta-row,
          .exam-schedule {
            padding: 0 12px 8px;
          }

          .countdown-section {
            margin: 0 12px 8px;
          }

          .search-input-wrapper {
            border-radius: 10px;
            height: 36px;
          }

          .search-input {
            font-size: 0.75rem;
            padding: 0.6rem 0.35rem 0.6rem 0;
          }

          .search-icon {
            padding: 0 0.6rem;
          }

          .clear-search-btn {
            width: 20px;
            height: 20px;
            margin-right: 0.35rem;
            font-size: 14px;
          }

          .filter-btn {
            width: 36px;
            height: 36px;
          }

          .pending-badge {
            min-width: 20px;
            height: 20px;
            font-size: 0.7rem;
          }
        }

        @media (max-width: 360px) {
          .section-header {
            padding: 0.75rem;
          }

          .search-input-wrapper {
            height: 34px;
          }

          .search-input {
            font-size: 0.7rem;
          }

          .search-icon {
            padding: 0 0.5rem;
          }

          .clear-search-btn {
            width: 18px;
            height: 18px;
            margin-right: 0.3rem;
            font-size: 12px;
          }

          .filter-btn {
            width: 34px;
            height: 34px;
          }
        }
      `}</style>
    </div>
  );
};

export default DashBoardExamListTable;